/* Totaria storefront behaviour. Custom elements clean up in disconnectedCallback, so theme
   editor section reloads never duplicate timers or listeners. No merchant content lives here. */

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
const fill = (tpl, map) => Object.keys(map).reduce((s, k) => s.split(`[${k}]`).join(map[k]), tpl || '');

/* ---------- Header offset for anchors and sticky elements ---------- */
function updateHeaderOffset() {
  const bar = document.querySelector('.tt-header--sticky .tt-header__bar');
  const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
  document.documentElement.style.setProperty('--tt-header-offset', `${h}px`);
  if (bar) document.body.style.setProperty('--header-height', `${h}px`);
}

/* ---------- In-page anchor links: offset + reduced-motion aware ---------- */
if (!window.__ttAnchorBound) {
  window.__ttAnchorBound = true;
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey) return;
    const url = new URL(link.href, window.location.href);
    if (url.pathname !== window.location.pathname || !url.hash || url.hash.length < 2) return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', url.hash);
  });
}

/* ---------- Announcement countdown ---------- */
class TtCountdown extends HTMLElement {
  connectedCallback() {
    this.end = Number(this.dataset.end) * 1000;
    const hours = Number(this.dataset.visitorHours);
    if (!this.end && hours > 0) {
      // Per-visitor countdown: starts on the first visit and never restarts in this browser.
      const key = 'tt-countdown-start';
      let start = 0;
      try { start = Number(localStorage.getItem(key)) || 0; } catch (_) {}
      if (!start) {
        start = Date.now();
        try { localStorage.setItem(key, String(start)); } catch (_) {}
      }
      this.end = start + hours * 3600 * 1000;
    }
    if (!this.end) return;
    this.units = {
      d: this.querySelector('[data-unit="d"]'),
      dChip: this.querySelector('[data-day-chip]'),
      h: this.querySelector('[data-unit="h"]'),
      m: this.querySelector('[data-unit="m"]'),
      s: this.querySelector('[data-unit="s"]'),
    };
    const sr = this.querySelector('[data-sr-end]');
    if (sr) {
      const t = Date.parse(sr.dataset.iso);
      if (!Number.isNaN(t)) {
        sr.textContent = `${sr.textContent.split('\n')[0].trim()} ${new Date(t).toLocaleString(document.documentElement.lang || undefined, { dateStyle: 'medium', timeStyle: 'short' })}`;
      }
    }
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }
  disconnectedCallback() { clearInterval(this.timer); }
  tick() {
    const left = Math.max(0, Math.floor((this.end - Date.now()) / 1000));
    if (left === 0) return this.expire();
    const pad = (n) => String(n).padStart(2, '0');
    const days = Math.floor(left / 86400);
    const useDays = !!this.units.d && days > 0;
    if (this.units.d) this.units.d.textContent = pad(days);
    if (this.units.dChip) this.units.dChip.hidden = !useDays;
    if (this.units.h) this.units.h.textContent = pad(useDays ? Math.floor((left % 86400) / 3600) : Math.floor(left / 3600));
    if (this.units.m) this.units.m.textContent = pad(Math.floor((left % 3600) / 60));
    if (this.units.s) this.units.s.textContent = pad(left % 60);
  }
  expire() {
    clearInterval(this.timer);
    if (this.dataset.expiredBehavior === 'text' && this.dataset.expiredText) {
      this.querySelector('[data-timer]')?.remove();
      this.querySelector('[data-sr-end]')?.remove();
      const text = this.querySelector('.tt-announce__text');
      if (text) text.textContent = this.dataset.expiredText;
    } else {
      this.hidden = true;
      this.style.display = 'none';
    }
    updateHeaderOffset();
  }
}
define('tt-countdown', TtCountdown);

/* ---------- Header + mobile menu drawer ---------- */
class TtHeader extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-menu-drawer]');
    this.opener = this.querySelector('[data-open-menu]');
    this.ac = new AbortController();
    const { signal } = this.ac;
    this.opener?.addEventListener('click', () => this.open(), { signal });
    this.querySelector('[data-close-menu]')?.addEventListener('click', () => this.close(), { signal });
    this.dialog?.addEventListener('cancel', (e) => { e.preventDefault(); this.close(); }, { signal });
    this.dialog?.addEventListener('click', (e) => { if (e.target === this.dialog) this.close(); }, { signal });
    this.dialog?.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => this.close(true), { signal }));
    window.matchMedia('(min-width: 900px)').addEventListener('change', (e) => { if (e.matches) this.close(true); }, { signal });

    this.ro = new ResizeObserver(updateHeaderOffset);
    this.ro.observe(this);
    updateHeaderOffset();

    // Keep the cart button's accessible name in sync with the real cart count.
    const trigger = this.querySelector('[data-tt-cart-trigger]');
    import('@shopify/events')
      .then(({ StandardEvents }) => {
        document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
          event.promise?.then(({ cart, detail }) => {
            const n = cart?.totalQuantity ?? detail?.itemCount ?? 0;
            if (trigger) trigger.setAttribute('aria-label', n === 1 ? trigger.dataset.labelOne : fill(trigger.dataset.labelOther, { count: n }));
          }).catch(() => {});
        }, { signal });
      })
      .catch(() => {});
  }
  disconnectedCallback() {
    this.ac?.abort();
    this.ro?.disconnect();
    document.documentElement.classList.remove('tt-lock');
  }
  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    document.documentElement.classList.add('tt-lock');
    this.opener?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => this.dialog.classList.add('is-open'));
    this.dialog.querySelector('[data-close-menu]')?.focus();
  }
  close(instant = false) {
    if (!this.dialog?.open) return;
    this.dialog.classList.remove('is-open');
    const done = () => {
      this.dialog.close();
      document.documentElement.classList.remove('tt-lock');
      this.opener?.setAttribute('aria-expanded', 'false');
      this.opener?.focus({ preventScroll: true });
    };
    if (instant || reducedMotion()) done();
    else setTimeout(done, 280);
  }
}
define('tt-header', TtHeader);

/* ---------- Botanical formula ---------- */
class TtFormula extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    const { signal } = this.ac;
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.pinned = null;
    this.buttons = [...this.querySelectorAll('[data-ingredient]')];
    this.buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        const wasPinned = this.pinned === btn;
        this.pinned = isOpen && wasPinned ? null : btn;
        this.show(this.pinned);
      }, { signal });
      btn.addEventListener('mouseenter', () => { if (fine.matches) this.show(btn); }, { signal });
      btn.addEventListener('mouseleave', () => { if (fine.matches) this.show(this.pinned); }, { signal });
      btn.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.pinned = null; this.show(null); } }, { signal });
    });
    const toggle = this.querySelector('[data-full-toggle]');
    const panel = this.querySelector('[data-full-panel]');
    toggle?.addEventListener('click', () => this.setFull(toggle.getAttribute('aria-expanded') !== 'true'), { signal });
    this.addEventListener('shopify:block:select', (e) => {
      const li = e.target.closest?.('[data-block-id]');
      if (panel?.contains(li)) this.setFull(true);
      const btn = li?.querySelector('[data-ingredient]');
      if (btn) { this.pinned = btn; this.show(btn); }
    }, { signal });
    this.toggle = toggle; this.panel = panel;
  }
  disconnectedCallback() { this.ac?.abort(); }
  show(active) {
    this.buttons.forEach((b) => b.setAttribute('aria-expanded', String(b === active)));
  }
  setFull(open) {
    if (!this.toggle || !this.panel) return;
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.toggle.dataset.labelOpen : this.toggle.dataset.labelClosed;
    this.panel.hidden = !open;
  }
}
define('tt-formula', TtFormula);

/* ---------- Reviews: show more ---------- */
class TtReviews extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    const btn = this.querySelector('[data-show-more]');
    btn?.addEventListener('click', () => {
      const extra = [...this.querySelectorAll('[data-extra][hidden]')];
      extra.forEach((el) => { el.hidden = false; });
      btn.parentElement.hidden = true;
      extra[0]?.setAttribute('tabindex', '-1');
      extra[0]?.focus({ preventScroll: false });
    }, { signal: this.ac.signal });
    this.addEventListener('shopify:block:select', (e) => {
      const card = e.target.closest?.('[data-extra]');
      if (card) card.hidden = false;
    }, { signal: this.ac.signal });
  }
  disconnectedCallback() { this.ac?.abort(); }
}
define('tt-reviews', TtReviews);

/* ---------- FAQ accordion ---------- */
class TtFaq extends HTMLElement {
  connectedCallback() {
    this.ac = new AbortController();
    const buttons = [...this.querySelectorAll('[data-faq-toggle]')];
    const set = (btn, open) => {
      btn.setAttribute('aria-expanded', String(open));
      const panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
    };
    buttons.forEach((btn, i) => {
      btn.addEventListener('click', () => set(btn, btn.getAttribute('aria-expanded') !== 'true'), { signal: this.ac.signal });
      btn.addEventListener('keydown', (e) => {
        const map = { ArrowDown: 1, ArrowUp: -1 };
        if (e.key === 'Home') { e.preventDefault(); buttons[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); buttons[buttons.length - 1].focus(); }
        else if (e.key in map) { e.preventDefault(); buttons[(i + map[e.key] + buttons.length) % buttons.length].focus(); }
      }, { signal: this.ac.signal });
    });
    this.addEventListener('shopify:block:select', (e) => {
      const btn = e.target.querySelector?.('[data-faq-toggle]');
      if (btn) set(btn, true);
    }, { signal: this.ac.signal });
  }
  disconnectedCallback() { this.ac?.abort(); }
}
define('tt-faq', TtFaq);

/* ---------- Newsletter (native Shopify customer form, enhanced) ---------- */
class TtNewsletter extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('[data-email]');
    this.button = this.querySelector('[data-submit]');
    this.msg = this.querySelector('[data-msg]');
    this.busy = false;
    this.ac = new AbortController();
    this.form?.addEventListener('submit', (e) => this.submit(e), { signal: this.ac.signal });
    this.input?.addEventListener('input', () => {
      if (this.msg.classList.contains('is-error')) this.say('', false);
    }, { signal: this.ac.signal });
  }
  disconnectedCallback() { this.ac?.abort(); }
  say(text, error) {
    this.msg.textContent = text;
    this.msg.classList.toggle('is-error', !!error);
    if (this.input) this.input.setAttribute('aria-invalid', error ? 'true' : 'false');
  }
  async submit(event) {
    event.preventDefault();
    if (this.busy) return;
    const email = (this.input?.value || '').trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.say(this.dataset.msgInvalid, true);
      this.input?.focus();
      return;
    }
    this.busy = true;
    const label = this.button.textContent;
    this.button.disabled = true;
    this.button.textContent = this.dataset.loading || label;
    try {
      const res = await fetch(this.form.action, { method: 'POST', body: new FormData(this.form), credentials: 'same-origin' });
      if (res.url.includes('/challenge')) { this.form.submit(); return; }
      const html = await res.text();
      const success = res.ok && (res.url.includes('customer_posted=true') || res.redirected && !res.url.includes('form_type'));
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const serverMsg = doc.getElementById(this.msg.id);
      const serverError = serverMsg?.dataset.serverError;
      if (success && !serverError) {
        this.querySelector('[data-row]')?.remove();
        this.say(this.dataset.msgSuccess, false);
      } else {
        this.say(serverError || this.dataset.msgError, true);
      }
    } catch (_) {
      this.say(this.dataset.msgError, true);
    } finally {
      this.busy = false;
      if (this.button.isConnected) { this.button.disabled = false; this.button.textContent = label; }
    }
  }
}
define('tt-newsletter', TtNewsletter);

document.addEventListener('shopify:section:load', updateHeaderOffset);
window.addEventListener('resize', updateHeaderOffset, { passive: true });